<template>
	<template v-if="data">
		<TheForm :model="data" :rules="rules" ref="formRef" :waiting="waiting" :id="formId">
			<ButtonSticky @buttonclick="saveStatement()" isSubmit>
				<template v-slot:icon><saveIcon /></template>
				<template v-slot:text> Speichern </template>
			</ButtonSticky>

			<section>
				<ButtonBack />

				<el-row class="titlerow">
					<el-col :sm="12">
						<h1>{{ localTitle }}</h1>
					</el-col>
					<el-col :sm="12">
						<div :class="`has-status has-status--${$getStateColor('statement', data.state)}`">
							{{ $getStateFullLabel('statement', data.state, false) }}
						</div>
					</el-col>
				</el-row>

				<RouterLink :to="'/fall/' + data.case?.id" :title="'Zugeordneten Fall anzeigen'" class="link" v-if="data.case && data.case.id">
					Zugeordneten Fall anzeigen
				</RouterLink>

				<el-row class="button-panel">
					<TheButton
						type="danger"
						@buttonclick="handleStatementReminder"
						:disabled="data.state === StatementState.Received || data.state === StatementState.StatementRejected ? true : false"
					>
						Reminder
					</TheButton>
					<TheButton
						type="danger"
						@buttonclick="openDialogExtendStatementDeadline = true"
						:disabled="
							data.lastValidDate ||
							data.state === StatementState.DeadlineExpired ||
							data.state === StatementState.StatementRejected ||
							data.state === StatementState.Received
								? true
								: false
						"
					>
						Frist verlängern
					</TheButton>
					<TheButton
						type="danger"
						@buttonclick="handleStatementRejected"
						:disabled="
							data.state === StatementState.StatementRejected ||
							data.state === StatementState.DeadlineExpired ||
							data.state === StatementState.Received
								? true
								: false
						"
					>
						Keine Stellungnahme erhalten
					</TheButton>
				</el-row>

				<TheBox :title="'Stellungnahme Infos'" :hasDataGrid="true">
					<el-row>
						<el-col :sm="12" :xs="24">
							<el-form-item label="Titel Stellungnahme:" prop="title">
								<el-tooltip effect="dark" :content="data.title" placement="top" :disabled="data.title.length < 40">
									<el-input v-model="data.title" :placeholder="'Bitte Titel der Stellungnahme eingeben'" />
								</el-tooltip>
							</el-form-item>

							<el-form-item label="Stn.-Gruppe:">
								<span>{{ data.contact?.roles?.length > 0 ? data.contact.roles.map((r) => r.name).join(', ') : '-' }}</span>
							</el-form-item>

							<el-form-item label="Stn. angefordert:">
								<span>{{ $formatDate(data.createdDate) }}</span>
							</el-form-item>

							<el-form-item label="Stn. erhalten:">
								<span>{{ data.answeredDate ? $formatDate(data.answeredDate) : '-' }}</span>
							</el-form-item>
						</el-col>

						<el-col :sm="12" :xs="24">
							<el-form-item label="Frist bis:">
								<span>{{ data.lastValidDate ? $formatDate(data.lastValidDate) : $formatDate(data.firstValidDate) }}</span>
							</el-form-item>

							<el-form-item label="Frist verlängert">
								<span>{{ data.lastValidDate ? 'Ja' : 'Nein' }}</span>
							</el-form-item>

							<el-form-item label="Frist Verlängerung bis:">
								<span>{{ data.lastValidDate ? $formatDate(data.lastValidDate) : '-' }}</span>
							</el-form-item>

							<el-form-item label="ID:">
								<span>{{ data.hid }}</span>
							</el-form-item>
						</el-col>
					</el-row>
				</TheBox>

				<TheBox :title="'Kontakt Stellungnahme'" :hasDataGrid="true">
					<el-row>
						<el-col :sm="12" :xs="24">
							<el-form-item label="Anrede:">
								<span>{{ data.contact.gender === Gender.Male ? 'Mann' : 'Frau' }}</span>
							</el-form-item>

							<el-form-item label="Titel (vor):">
								<span>{{ data.contact.titleBefore }}</span>
							</el-form-item>

							<el-form-item label="Titel (nach):">
								<span>{{ data.contact.titleAfter }}</span>
							</el-form-item>

							<el-form-item label="Vorname:">
								<span>{{ data.contact.firstName }}</span>
							</el-form-item>
						</el-col>

						<el-col :sm="12" :xs="24">
							<el-form-item label="Nachname:">
								<span>{{ data.contact.lastName }}</span>
							</el-form-item>

							<el-form-item label="E-Mail-Adresse:">
								<span>{{ data.contact.email }}</span>
							</el-form-item>

							<el-form-item label="Firma:">
								<span>{{ data.contact.company ? data.contact.company : '-' }}</span>
							</el-form-item>

							<el-form-item label="Telefon:">
								<span>{{ data.contact.phoneNumber ? data.contact.phoneNumber : '-' }}</span>
							</el-form-item>
						</el-col>
					</el-row>
				</TheBox>

				<TheBox :title="'Stellungnahme:'">
					<el-form-item label="" prop="answer">
						<el-input v-model="data.answer" :autosize="{ minRows: 6 }" type="textarea" placeholder="Bitte Stellungnahme hier eingeben." />
					</el-form-item>
				</TheBox>
			</section>

			<DetailsMedia
				:files="statementFiles"
				:allowDeleteFromServer="true"
				@addToFiles="addToFiles"
				@removeFromFiles="removeFromFiles"
				:pageType="'stellungnahme'"
			/>
			<DetailsLinks
				:links="statementLinks"
				@addToLinks="addToLinks"
				@removeFromLinks="removeFromLinks"
				:pageType="'stellungnahme'"
				:allowDeleteFromServer="true"
			/>

			<section>
				<TheBox :title="'Interne Notiz (nur für Mitarbeiter sichtbar)'">
					<el-input
						v-model="data.internalNote"
						:autosize="{ minRows: 3 }"
						type="textarea"
						placeholder="Schreiben Sie hier Ihre Notiz. Ihre Notiz ist nur für Mitarbeiter sichtbar."
					/>
				</TheBox>

				<TheButton type="default" @buttonclick="openDialogDeleteStatement = true">
					<el-icon><Delete /></el-icon>
					Stellungnahme löschen
				</TheButton>
			</section>
		</TheForm>

		<DialogExtendStatementDeadline
			v-model:open="openDialogExtendStatementDeadline"
			:statementId="id"
			:deadlineDate="data.firstValidDate"
			@reload="loadStatement"
		/>
		<DialogDeleteStatement
			v-model:open="openDialogDeleteStatement"
			:statementId="id"
			:caseId="data.case.id"
			@close="openDialogDeleteStatement = false"
		/>

		<DialogLeavePage v-model:open="openClosingDialog" @cancel="leavePage" @save="saveStatement(true)" />
	</template>
	<section v-else-if="loading === false">
		<ButtonBack />
		<el-alert :title="texts.general.emptyText" type="error" effect="dark" :closable="false" />
	</section>
</template>

<script lang="ts">
import ComplaintsTable from '@/components/tables/ComplaintsTable.vue';
import StatementsTable from '@/components/tables/StatementsTable.vue';
import { defineComponent, ref, markRaw } from 'vue';
import { Delete, Plus, Loading } from '@element-plus/icons-vue';
import type { FormInstance } from 'element-plus';
import { ElMessage } from 'element-plus';
import saveIcon from '../assets/icons/saveIcon.vue';
import ButtonBack from '../components/buttons/ButtonBack.vue';
import ButtonSticky from '../components/buttons/ButtonSticky.vue';
import DialogExtendStatementDeadline from '../components/dialogs/DialogExtendStatementDeadline.vue';
import DialogLeavePage from '../components/dialogs/DialogLeavePage.vue';
import TheBox from '../components/TheBox.vue';
import TheButton from '../components/TheButton.vue';
import TheForm from '../components/TheForm.vue';
import { getStatement, putStatement, putStatementState, sendStatementReminder } from '@/requests/statements';
import { PutStatementRequest } from '@/types/formdataTypes';
import { startLoading, stopLoading } from '@/utils/loading/loadingHandler';
import { DetailFile, DetailLink, FileDto, Gender, LinkDto, StatementResponse, StatementState } from '../types/types';
import { validateForm, hasFormChanged } from '../utils/functions';
import { texts } from '../utils/texts.js';
import DetailsLinks from './sections/DetailsLinks.vue';
import DetailsMedia from './sections/DetailsMedia.vue';
import cloneDeep from 'lodash/cloneDeep';
import DialogDeleteStatement from '@/components/dialogs/DialogDeleteStatement.vue';

const ElMessageDuration = 3000;

export default defineComponent({
	name: 'StellungnahmeDetail',
	data() {
		return {
			data: null as StatementResponse | null,
			initialData: null as StatementResponse | null,
			// saveCaseRequest: null as SaveStatementRequest | null,
			formId: 'statementForm',
			rules: {
				title: [{ required: true, message: texts.formErrors.reqMissing, trigger: 'blur' }],
				answer: [
					{ required: true, message: texts.formErrors.reqMissing, trigger: 'blur' },
					{ min: 3, message: texts.formErrors.minChars, trigger: 'blur' },
				],
			},
			waiting: false,
			loading: true,
			localTitle: '',
			statementFiles: [] as DetailFile[],
			statementLinks: [] as DetailLink[],
			openDialogExtendStatementDeadline: false,
			openDialogDeleteStatement: false,
			openClosingDialog: false,
			checkPageLeave: true,
			pageLeaveTo: null,
			Gender,
			StatementState,
		};
	},
	props: {
		id: {
			type: String,
			required: true,
		},
	},
	components: {
		ButtonBack,
		ButtonSticky,
		TheButton,
		TheForm,
		TheBox,
		Plus,
		Delete,
		Loading,
		DetailsMedia,
		DetailsLinks,
		saveIcon,
		ComplaintsTable,
		StatementsTable,
		DialogExtendStatementDeadline,
		DialogDeleteStatement,
		DialogLeavePage,
	},
	setup() {
		const formRef = ref<FormInstance | null>(null);

		return {
			formRef,
		};
	},
	computed: {
		texts() {
			return texts;
		},
	},
	methods: {
		async saveStatement(leavePage = false) {
			if (!this.data) return;

			const isValid = await validateForm(this.formRef, this.formId);
			if (!isValid) return;

			this.waiting = true;
			const waitingMsg = ElMessage({ message: texts.formErrors.waiting, type: 'info', duration: 0, showClose: false, icon: markRaw(Loading) });

			const saveStatementRequest: PutStatementRequest = {
				statementId: this.data.id,
				title: this.data.title,
				answer: this.data.answer ? this.data.answer : '',
				internalNote: this.data.internalNote ? this.data.internalNote : '',
			};

			const newLinks = this.statementLinks
				.filter((l) => l.isNew)
				.map((l) => l.link)
				.map((l) => {
					return { name: l.name, url: l.url, type: l.type } as LinkDto;
				});
			if (newLinks.length > 0) saveStatementRequest.links = newLinks;
			const newFiles = this.statementFiles
				.filter((f) => f.isNew)
				.map((f) => f.file)
				.map((f) => {
					return { fileName: f.fileName, contentType: f.contentType, bytes: f.bytes } as FileDto;
				});
			if (newFiles.length > 0) saveStatementRequest.files = newFiles;

			const saveResult = await putStatement(saveStatementRequest);
			if (saveResult.success) {
				if (leavePage) this.leavePage();
				else {
					this.loadStatement();

					// setze alle files und links auf isNew = false damit sie nicht doppelt hochgeladen werden
					this.statementFiles = this.statementFiles.map((f) => {
						return { file: f.file, isNew: false };
					});
					this.statementLinks = this.statementLinks.map((l) => {
						return { link: l.link, isNew: false };
					});

					ElMessage({ message: this.texts.statements.saveSuccess, type: 'success', duration: ElMessageDuration });
				}
			} else {
				ElMessage({ message: this.texts.statements.saveFailed, type: 'error', duration: ElMessageDuration });
			}

			this.waiting = false;
			waitingMsg.close();
		},
		async loadStatement() {
			const loading = startLoading('#stellungnahme');

			const result = await getStatement(this.id);
			if (result.success) {
				this.data = result.data;
				this.initialData = cloneDeep(result.data);

				this.localTitle = this.data.title;
				this.statementLinks = this.data.links.map((l) => {
					return { link: l, isNew: false } as DetailLink;
				});
				this.statementFiles = this.data.files.map((f) => {
					return { file: f, isNew: false } as DetailFile;
				});
			}

			this.loading = false;
			stopLoading(loading);
		},

		async handleStatementRejected() {
			const loading = startLoading('#stellungnahme');

			const putStatementStateResult = await putStatementState({
				statementId: this.id,
				state: StatementState.StatementRejected,
			});
			if (putStatementStateResult.success) {
				this.loadStatement();
			} else {
				ElMessage({ message: this.texts.statements.saveFailed, type: 'error', duration: ElMessageDuration });
			}

			stopLoading(loading);
		},
		addToFiles(file) {
			this.statementFiles.push(file);
		},
		removeFromFiles(file) {
			this.statementFiles = this.statementFiles.filter((f) => f.file !== file.file);
		},
		removeFromLinks(link) {
			this.statementLinks = this.statementLinks.filter((l) => l.link !== link.link);
		},
		addToLinks(link) {
			this.statementLinks.push(link);
		},
		async handleStatementReminder() {
			if (!this.data) return;
			const loading = startLoading('#stellungnahme');

			const sendStatementReminderResult = await sendStatementReminder({
				statementId: this.id,
			});
			if (sendStatementReminderResult.success) {
				ElMessage({ message: this.texts.statements.reminderSuccess, type: 'success', duration: ElMessageDuration });
			} else {
				ElMessage({ message: this.texts.statements.reminderFailed, type: 'error', duration: ElMessageDuration });
			}

			stopLoading(loading);
		},
		leavePage() {
			this.openClosingDialog = false;
			this.checkPageLeave = false;
			// @ts-ignore
			this.$router.push(this.pageLeaveTo);
		},
		handleBeforeRouteLeave(to, from, next) {
			if (this.checkPageLeave === false) {
				next();
				return;
			}

			// check if request data has changed
			const hasChanged = hasFormChanged(
				{ initialData: this.initialData, currentData: this.data },
				this.statementFiles.filter((f) => f.isNew),
				this.statementLinks.filter((l) => l.isNew),
			);

			if (!hasChanged) {
				next();
				return;
			}

			// ABORT NAVIGATION & SHOW DIALOG
			this.openClosingDialog = true;
			this.pageLeaveTo = to;
			next(false);
		},
	},
	beforeRouteLeave(to, from, next) {
		this.handleBeforeRouteLeave(to, from, next);
	},
	mounted() {
		this.loadStatement();
	},
});
</script>
